// import React from 'react';

// import FBieWorkbenchShell, {
//   FWorkbenchShellProps,
// } from './FBieWorkbenchShell';
// import PlanningContextRequired from './PlanningContextRequired';
// import { PlanningContextPageProps } from './planningcontext.pageprops.patch';
// import { requirePlanningCycle } from './cycle-bound-page.guard';

// export interface PlanningWorkbenchShellProps
//   extends Omit<
//       FWorkbenchShellProps,
//       | 'planningContext'
//       | 'showPlanningContext'
//       | 'onChangePlanningCycle'
//       | 'switchingPlanningContext'
//       | 'children'
//     >,
//     PlanningContextPageProps {
//   /** Most planning pages are cycle-bound. Planning Control can set this false. */
//   requireCycle?: boolean;
//   contextRequiredTitle?: string;
//   contextRequiredMessage?: string;
//   children: React.ReactNode;
// }

// /**
//  * Standard shell for BIE Historical Data through Activity Monitoring.
//  *
//  * It always renders the workbench chrome and context banner. When a page is
//  * cycle-bound and no cycle is selected, only the content area is guarded; the
//  * shell, Back, Refresh, alerts and context switch action remain available.
//  */
// export default class PlanningWorkbenchShell extends React.PureComponent<PlanningWorkbenchShellProps> {
//   openPlanningCycleSwitcher = () => {
//     if (this.props.openPlanningCycleSwitcher){
//       this.props.openPlanningCycleSwitcher();
//     }
//   }
//   public render(): React.ReactNode {
//     const {
//       plancontext,
//       planningContextSwitching,
//       openPlanningCycleSwitcher,
//       requireCycle = true,
//       contextRequiredTitle,
//       contextRequiredMessage,
//       children,
//       ...shellProps
//     } = this.props;

//     const guard = requirePlanningCycle(plancontext);
//     const content = requireCycle && !guard.ready ? (
//       <PlanningContextRequired
//         title={contextRequiredTitle}
//         message={contextRequiredMessage || guard.message}
//         onOpenPlanningCycle={this.openPlanningCycleSwitcher}
//       />
//     ) : (
//       children
//     );

//     return (
//       <FBieWorkbenchShell
//         {...shellProps}
//         planningContext={plancontext}
//         showPlanningContext
//         onChangePlanningCycle={openPlanningCycleSwitcher}
//         switchingPlanningContext={planningContextSwitching}
//       >
//         {content}
//       </FBieWorkbenchShell>
//     );
//   }
// }


import React from 'react';

import FBieWorkbenchShell, {
  FWorkbenchShellProps,
} from './FBieWorkbenchShell';
import PlanningContextRequired from './PlanningContextRequired';
import { PlanningContextPageProps } from './planningcontext.pageprops.patch';
import { requirePlanningCycle } from './cycle-bound-page.guard';

import {
  fetchPlanningJourneyPhases,
  getCachedPlanningJourneyPhases,
} from '../core/planningJourney.cache';

export interface PlanningRuntimeContext {
  planningcycleid?: number;
  phasecode?: string;
  phasename?: string;
}

export interface PlanningWorkbenchShellProps
  extends Omit<
    FWorkbenchShellProps,
    | 'planningContext'
    | 'showPlanningContext'
    | 'onChangePlanningCycle'
    | 'switchingPlanningContext'
    | 'children'
    | 'phases'
    | 'phasecode'
    | 'governancePhaseCode'
    | 'workspacePhaseCode'
  >,
  PlanningContextPageProps {

  requireCycle?: boolean;
  contextRequiredTitle?: string;
  contextRequiredMessage?: string;

  /** Mutable DB-authoritative runtime state returned by the workbench. */
  runtimeContext?: PlanningRuntimeContext | null;

  /** Identity of the workbench currently being rendered. */
  workspacePhaseCode?: string | null;

  children: React.ReactNode;
}

type State = {
  phases: any[];
};

export default class PlanningWorkbenchShell extends React.PureComponent<
  PlanningWorkbenchShellProps,
  State
> {
  state: State = {
    phases: getCachedPlanningJourneyPhases(),
  };

  componentDidMount(): void {
    this.loadJourneyPhases();
  }

  private loadJourneyPhases = async (): Promise<void> => {
    try {
      const phases = await fetchPlanningJourneyPhases();
      this.setState({ phases });
    } catch {
      /*
       * Journey rendering is supplementary workbench chrome.
       * Failure must not prevent the actual workbench from operating.
       */
    }
  };

  private normalizeCode = (value?: string | null): string =>
    String(value || '').trim().toUpperCase();

  private isPlanningSubsystemWorkspace = (
    workspacePhaseCode: string,
  ): boolean => {
    return [
      'BUDGET_CONTROL',
      'DIRECT_BUDGET',
      'INVESTMENT_BUDGET',
      'ACTIVITY_PLANNING',
      'ACTIVITY_PROGRAMME',
      'BUDGET_ALLOCATION',
      'BUDGET_CONSOLIDATION',
      'BUDGET_VALIDATION',
      'PPM_PROCESSING',
      'PPM_REPORT',
      'GOVERNANCE',
    ].includes(workspacePhaseCode);
  };

  private resolveDisplayPhaseCode = (): string => {
    const governancePhaseCode = this.normalizeCode(
      this.props.runtimeContext?.phasecode,
    );

    const workspacePhaseCode = this.normalizeCode(
      this.props.workspacePhaseCode,
    );

    if (
      governancePhaseCode === 'BUDGET_CONTROL' &&
      workspacePhaseCode &&
      this.isPlanningSubsystemWorkspace(workspacePhaseCode)
    ) {
      return workspacePhaseCode;
    }

    return governancePhaseCode;
  };

  openPlanningCycleSwitcher = () => {
    if (this.props.openPlanningCycleSwitcher) {
      this.props.openPlanningCycleSwitcher();
    }
  };

  public render(): React.ReactNode {
    const {
      plancontext,
      planningContextSwitching,
      openPlanningCycleSwitcher,
      requireCycle = true,
      contextRequiredTitle,
      contextRequiredMessage,
      runtimeContext,
      workspacePhaseCode,
      children,
      ...shellProps
    } = this.props;

    const guard = requirePlanningCycle(plancontext);

    const content =
      requireCycle && !guard.ready ? (
        <PlanningContextRequired
          title={contextRequiredTitle}
          message={contextRequiredMessage || guard.message}
          onOpenPlanningCycle={this.openPlanningCycleSwitcher}
        />
      ) : (
        children
      );

    const governancePhaseCode = this.normalizeCode(
      runtimeContext?.phasecode,
    );

    const displayPhaseCode = this.resolveDisplayPhaseCode();

    return (
      <FBieWorkbenchShell
        {...shellProps}
        planningContext={plancontext}
        showPlanningContext
        onChangePlanningCycle={openPlanningCycleSwitcher}
        switchingPlanningContext={planningContextSwitching}

        phases={this.state.phases}
        phasecode={displayPhaseCode}

        governancePhaseCode={governancePhaseCode}
        workspacePhaseCode={this.normalizeCode(workspacePhaseCode)}
      >
        {content}
      </FBieWorkbenchShell>
    );
  }
}